import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

function CreateProject() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(projectId));
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        clientId: '',
        address: '',
        mobileNumber: '',
        email: ''
    });

    useEffect(() => {
        withLoading(async () => {
            const clientsRes = await api.get('/clients');
            setClients(clientsRes.data);

            if (projectId) {
                const projectRes = await api.get(`/projects/${projectId}`);
                setFormData({
                    name: projectRes.data.name || '',
                    clientId: String(projectRes.data.clientId || ''),
                    address: projectRes.data.address || '',
                    mobileNumber: projectRes.data.mobileNumber || '',
                    email: projectRes.data.email || ''
                });
            }
        }).catch(() => {
            alert('Error loading project data');
        });
    }, [projectId, withLoading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            name: formData.name.trim(),
            clientId: Number(formData.clientId),
            address: formData.address.trim(),
            mobileNumber: formData.mobileNumber.trim() || null,
            email: formData.email.trim() || null
        };

        const apiCall = projectId
            ? api.put(`/projects/${projectId}`, payload)
            : api.post('/projects', payload);

        apiCall
            .then(() => {
                navigate('/admin/projects');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error saving project details';
                alert(message);
            });
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{projectId ? 'Edit project' : 'Add project'}</h1>
                        <p className="form-page-subtitle">
                            {projectId
                                ? 'Update project details and linked client'
                                : 'Enter project details to add them to your records'}
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/admin/projects')}
                        >
                            Back to list
                        </button>
                        <button
                            type="submit"
                            form="create-project-form"
                            className="form-page-btn"
                        >
                            {projectId ? 'Update project' : 'Save project'}
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Project details</h2>
                            <p className="form-page-section-hint">Project name, client, and contact information</p>
                        </div>
                    </div>

                    <form id="create-project-form" onSubmit={handleSubmit}>
                        <div className="form-page-grid cols-3">
                            <div className="form-page-field span-full">
                                <label htmlFor="name">Project name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter project name"
                                />
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="clientId">Client</label>
                                <select
                                    id="clientId"
                                    name="clientId"
                                    value={formData.clientId}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select a client</option>
                                    {clients.map(client => (
                                        <option key={client.id} value={client.id}>
                                            {client.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="address">Address</label>
                                <textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    rows="3"
                                    required
                                    placeholder="Enter project address"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="mobileNumber">Mobile number (optional)</label>
                                <input
                                    type="tel"
                                    id="mobileNumber"
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleChange}
                                    placeholder="Enter mobile number"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="email">Email (optional)</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter email address"
                                />
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </PageLoadingArea>
    );
}

export default CreateProject;
